import { useMutation, useQuery, useQueryClient, queryOptions } from "@tanstack/react-query";
import { parseResponse } from "lib/fetch";
import { createBookingValidationRequest, getBookingValidationRequest } from "lib/api/bookingValidation";
import type { BookingValidationInterface } from "types/bookingValidation";

export { BookingValidationStatus } from "types/bookingValidation";
export type { BookingValidationInterface } from "types/bookingValidation";

export const bookingValidationKeys = {
    all: ["booking-validation"] as const,
    details: () => [...bookingValidationKeys.all, "detail"] as const,
    detail: (id: string) => [...bookingValidationKeys.details(), id] as const,
};

export const createBookingValidation = (data: HashMap<unknown>) =>
    parseResponse<BookingValidationInterface>(createBookingValidationRequest(data));

export const fetchBookingValidation = (id: string) =>
    parseResponse<BookingValidationInterface>(getBookingValidationRequest(id));

export const bookingValidationDetailQueryOptions = (id: string | undefined) =>
    queryOptions<BookingValidationInterface>({
        queryKey: id ? bookingValidationKeys.detail(id) : bookingValidationKeys.detail("__missing__"),
        queryFn: () => {
            if (!id) return Promise.reject(new Error("Missing booking validation id"));
            return fetchBookingValidation(id);
        },
        enabled: Boolean(id),
    });

export const useBookingValidation = (id: string | undefined) => useQuery(bookingValidationDetailQueryOptions(id));

export const useCreateBookingValidation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data: HashMap<unknown>) => createBookingValidation(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: bookingValidationKeys.all });
        },
    });
};

const bookingValidationDataFetcher = {
    create: createBookingValidation,
    one: fetchBookingValidation,
};

export default bookingValidationDataFetcher;
